import get from 'lodash/get';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {createSelector} from 'reselect';
import type {RootState} from '../../types';
import type {ConfigurationsState, ReceiveCountryCurrencyRatesPayload, SitePreferences} from './types';
import {ECMSMode} from './types';

const configurationsSelector = (state: RootState) => state.configurations;

export const configurationsInitialState: ConfigurationsState = {
	sitePreferences: {} as SitePreferences,
	organizationPreferences: {},
	passwordConfig: {
		forceLetters: true,
		forceMixedCase: true,
		forceNumbers: true,
		maxLength: 16,
		minLength: 8,
		minSpecialChars: 1,
	},
	countries: [],
	languages: [],
	currentCountryCode: '',
	currentLanguageCode: '',
	siteChangingStatus: '',
	geolocation: {},
	cms: {
		mode: ECMSMode.LIVE,
	},
	currencyConversionRates: {},
	customerGroups: [],
};

export const configurationsSlice = createSlice({
	name: 'configurations',
	initialState: configurationsInitialState,
	reducers: {
		receiveConfigurations: (state: ConfigurationsState, action: PayloadAction<Partial<ConfigurationsState>>) => {
			return Object.assign({}, state, action.payload);
		},
		receiveCountries: (state: ConfigurationsState, action: PayloadAction<ConfigurationsState['countries']>) => {
			return Object.assign({}, state, {countries: action.payload});
		},
		receiveLanguages: (state: ConfigurationsState, action: PayloadAction<ConfigurationsState['languages']>) => {
			return Object.assign({}, state, {languages: action.payload});
		},
		receiveCurrentCountryCode: (state: ConfigurationsState, action: PayloadAction<string>) => {
			return Object.assign({}, state, {currentCountryCode: action.payload});
		},
		receiveCurrentLanguageCode: (state: ConfigurationsState, action: PayloadAction<string>) => {
			return Object.assign({}, state, {currentLanguageCode: action.payload});
		},
		receiveSiteChangingStatus: (state: ConfigurationsState, action: PayloadAction<string>) => {
			return Object.assign({}, state, {siteChangingStatus: action.payload});
		},
		receiveCms: (state: ConfigurationsState, action: PayloadAction<ConfigurationsState['cms']>) => {
			return Object.assign({}, state, {cms: action.payload});
		},
		receiveCountryCurrencyRates: (
			state: ConfigurationsState,
			action: PayloadAction<ReceiveCountryCurrencyRatesPayload>,
		) => {
			const currencyConversionRates = state.currencyConversionRates || {};

			return Object.assign({}, state, {
				currencyConversionRates: {
					...state.currencyConversionRates,
					[action.payload.from]: {
						...currencyConversionRates[action.payload.from],
						[action.payload.to]: action.payload.rate,
					},
				},
			});
		},
	},
});

export const {
	receiveConfigurations,
	receiveCountries,
	receiveLanguages,
	receiveCurrentCountryCode,
	receiveCurrentLanguageCode,
	receiveSiteChangingStatus,
	receiveCms,
	receiveCountryCurrencyRates,
} = configurationsSlice.actions;

export const selectConfigurations = createSelector(configurationsSelector, (configurations) => configurations);
export const selectCountries = createSelector(configurationsSelector, ({countries}) => countries);
export const selectLanguages = createSelector(configurationsSelector, ({languages}) => languages);
export const selectCurrentCountryCode = createSelector(
	configurationsSelector,
	({currentCountryCode}) => currentCountryCode,
);
export const selectCurrentLanguageCode = createSelector(
	configurationsSelector,
	({currentLanguageCode}) => currentLanguageCode,
);
export const selectSiteChangingStatus = createSelector(
	configurationsSelector,
	({siteChangingStatus}) => siteChangingStatus
);

export const selectConfiguration = (state: RootState, prop: string) =>
	createSelector(selectConfigurations, (configurations) => get(configurations, prop))(state);

export const selectCms = createSelector(configurationsSelector, ({cms}) => cms);
export const selectCurrencyConversionRates = createSelector(
	configurationsSelector,
	({currencyConversionRates}) => currencyConversionRates,
);

export default configurationsSlice.reducer;
